import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * TocScrollExtension
 *
 * The table-of-contents callout block renders its entries as inline `Link`
 * marks whose `href` is a bare same-document fragment (`#<slug>`). Matching
 * heading nodes carry that slug as either a DOM `id` or `data-toc-id`
 * attribute (see BlockIdAttribute.ts).
 *
 * `Link.configure({ openOnClick: false })` already stops the Link
 * extension's own click handler from opening the URL in a new tab, but it
 * doesn't implement the desired behavior — clicking a TOC entry did
 * nothing. This extension intercepts primary clicks on `a[href^="#"]`
 * inside the editor DOM, prevents default navigation, resolves the target
 * heading, and smooth-scrolls it into view.
 *
 * Cmd/Ctrl/Shift/Alt-clicks and non-fragment links are left alone so
 * default browser behavior (e.g. opening a real URL in a new tab) still
 * works.
 */

/**
 * Attribute marker for any sticky editor-chrome element (e.g. the internal
 * formatting toolbar) that overlaps the top of the editor's scroll
 * container. When present, its rendered height is applied to the scroll
 * target as `scroll-margin-top` so the destination heading isn't hidden
 * underneath it.
 */
export const TOC_STICKY_CHROME_ATTR = 'data-editor-sticky-chrome';

function isPrimaryUnmodifiedClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function cssEscape(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(id);
  }
  // Minimal fallback for environments without CSS.escape (should be rare).
  return id.replace(/([^a-zA-Z0-9_-])/g, '\\$1');
}

/**
 * Resolves the element a `#fragment` TOC link should scroll to, searching
 * `root` for a matching `id` first, then `data-toc-id`.
 */
export function resolveTocScrollTarget(root: ParentNode, hash: string): HTMLElement | null {
  const id = hash.replace(/^#/, '').trim();
  if (!id) return null;

  const escaped = cssEscape(id);
  return (
    root.querySelector<HTMLElement>(`#${escaped}`) ??
    root.querySelector<HTMLElement>(`[data-toc-id="${escaped}"]`)
  );
}

/**
 * Handles a native click event anywhere inside the editor DOM (`root`).
 * Returns `true` when it intercepted a same-document TOC link — the caller
 * (ProseMirror's `handleDOMEvents.click`) should stop further handling.
 * Returns `false` to let default/ProseMirror handling proceed untouched.
 */
export function handleTocScrollClick(root: HTMLElement, event: MouseEvent): boolean {
  if (event.defaultPrevented || !isPrimaryUnmodifiedClick(event)) return false;

  const clicked = event.target as HTMLElement | null;
  const anchor = clicked?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
  if (!anchor || !root.contains(anchor)) return false;

  const href = anchor.getAttribute('href') ?? '';
  if (href.length <= 1) return false; // bare "#" or empty — nothing to resolve

  // From here on this is a TOC-style fragment click: stop the browser from
  // navigating (which previously manifested as a new tab / URL jump).
  event.preventDefault();

  const heading = resolveTocScrollTarget(root, href);
  if (!heading) return true;

  const stickyChrome = root.querySelector<HTMLElement>(`[${TOC_STICKY_CHROME_ATTR}]`);
  const stickyOffset = stickyChrome?.getBoundingClientRect().height ?? 0;
  heading.style.scrollMarginTop = stickyOffset > 0 ? `${stickyOffset}px` : '';

  heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}

export const TocScrollExtension = Extension.create({
  name: 'tocScroll',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('tocScroll'),
        props: {
          handleDOMEvents: {
            click: (view, event) => handleTocScrollClick(view.dom as HTMLElement, event),
          },
        },
      }),
    ];
  },
});
